/**
 * Request history — last N try-it requests persisted to localStorage.
 *
 * Capped at 50 entries (FIFO). Stored under a versioned key so we can evolve
 * the schema without crashing existing tabs.
 */

import { useSyncExternalStore } from 'react';

export interface HistoryEntry {
  id: string;
  timestamp: number;
  operationId: string;
  method: string;
  path: string;
  status: number;
  ok: boolean;
  durationMs: number;
}

const KEY = 'playground:history:v1';
const MAX = 50;

function load(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

let state: HistoryEntry[] = load();
const listeners = new Set<() => void>();

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

export function recordRequest(entry: Omit<HistoryEntry, 'id' | 'timestamp'>) {
  const newEntry: HistoryEntry = {
    ...entry,
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    timestamp: Date.now(),
  };
  state = [newEntry, ...state].slice(0, MAX);
  persist();
  for (const l of listeners) l();
}

export function clearHistory() {
  state = [];
  persist();
  for (const l of listeners) l();
}

export function useHistory(): HistoryEntry[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state
  );
}
